import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MessageSquarePlus, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { MessageList, Composer, Avatar, WA, chatTime } from "@/components/chat/ChatParts";

// Admin "Chat" tab — WhatsApp-style list of every user's thread + one-to-one conversation.
export default function AdminChat() {
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [picker, setPicker] = useState(false);

  const loadThreads = useCallback(() => api.chatThreads(q).then((r) => setThreads(r.threads)).catch(() => {}), [q]);
  useEffect(() => { loadThreads(); const iv = setInterval(loadThreads, 8000); return () => clearInterval(iv); }, [loadThreads]);
  useEffect(() => {
    if (!sel) return undefined;
    const load = () => api.chatThread(sel.user_id).then((r) => setMsgs(r.messages)).catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [sel]);

  const open = (t) => { setSel(t); setMsgs([]); setThreads((l) => l.map((x) => (x.user_id === t.user_id ? { ...x, unread: 0 } : x))); };
  const send = async (text, file) => {
    try { const m = await api.chatAdminSend(sel.user_id, text, file); setMsgs((l) => [...l, m]); loadThreads(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); throw e; }
  };

  return (
    <div className="flex h-[78vh] overflow-hidden rounded-xl border border-white/10" style={{ background: WA.panel }} data-testid="admin-chat">
      <div className={`${sel ? "hidden md:flex" : "flex"} w-full flex-col border-r border-white/10 md:w-[360px]`}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: WA.head }}>
          <span className="text-base font-bold text-[#e9edef]">Chats</span>
          <button onClick={() => setPicker((v) => !v)} data-testid="admin-chat-new" title="New chat" className="text-[#aebac1] hover:text-white"><MessageSquarePlus size={20} /></button>
        </div>
        <div className="px-3 py-2">
          <div className="flex items-center gap-2 rounded-lg px-3 py-1.5" style={{ background: WA.head }}>
            <Search size={15} className="text-[#8696a0]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={picker ? "Search users to start a chat" : "Search or start new chat"} data-testid="admin-chat-search"
              className="flex-1 bg-transparent text-sm text-[#e9edef] outline-none placeholder:text-[#8696a0]" />
          </div>
        </div>
        {picker ? <UserPicker q={q} onPick={(u) => { setPicker(false); setQ(""); open({ ...u, unread: 0 }); }} /> : (
          <div className="flex-1 overflow-y-auto" data-testid="admin-chat-list">
            {threads.map((t) => (
              <button key={t.user_id} onClick={() => open(t)} data-testid={`admin-chat-thread-${t.user_id}`}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#202c33] ${sel?.user_id === t.user_id ? "bg-[#2a3942]" : ""}`}>
                <Avatar name={t.name} picture={t.picture} size={46} />
                <div className="min-w-0 flex-1 border-b border-white/5 pb-2.5">
                  <div className="flex items-center justify-between gap-2"><span className="truncate text-[15px] text-[#e9edef]">{t.name}</span>
                    <span className="shrink-0 text-[11px]" style={{ color: t.unread ? WA.green : WA.muted }}>{chatTime(t.last.created_at)}</span></div>
                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1 truncate text-[13px]" style={{ color: WA.muted }}>
                      {t.last.from_admin && "You: "}{t.last.has_image && <ImageIcon size={13} className="shrink-0" />}{t.last.text || (t.last.has_image ? "Photo" : "")}
                    </span>
                    {t.unread > 0 && <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-[#111b21]" style={{ background: WA.green }} data-testid={`admin-chat-unread-${t.user_id}`}>{t.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
            {!threads.length && <p className="px-6 py-10 text-center text-sm" style={{ color: WA.muted }}>No chats yet. Messages from users in the editor appear here.</p>}
          </div>
        )}
      </div>
      <div className={`${sel ? "flex" : "hidden md:flex"} flex-1 flex-col`}>
        {sel ? (<>
          <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: WA.head }} data-testid="admin-chat-header">
            <button onClick={() => setSel(null)} className="text-[#aebac1] md:hidden"><ArrowLeft size={18} /></button>
            <Avatar name={sel.name} picture={sel.picture} size={40} />
            <div className="min-w-0"><div className="truncate text-[15px] text-[#e9edef]">{sel.name}</div><div className="truncate text-xs" style={{ color: WA.muted }}>{sel.email}</div></div>
          </div>
          <MessageList messages={msgs} mine={(m) => m.from_admin} testId="admin-chat-messages" />
          <Composer onSend={send} testId="admin-chat" />
        </>) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center" style={{ background: WA.head }}>
            <div className="text-2xl font-light text-[#e9edef]">Caption Motion Chat</div>
            <p className="max-w-sm text-sm" style={{ color: WA.muted }}>Pick a chat on the left to reply. Each user can only talk to the admin team.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const UserPicker = ({ q, onPick }) => {
  const [users, setUsers] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.chatUserSearch(q).then((r) => setUsers(r.users)).catch(() => {}), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="flex-1 overflow-y-auto" data-testid="admin-chat-user-picker">
      {users.map((u) => (
        <button key={u.user_id} onClick={() => onPick({ ...u, name: u.name || u.email })} data-testid={`admin-chat-pick-${u.user_id}`} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-[#202c33]">
          <Avatar name={u.name || u.email} picture={u.picture} size={40} />
          <div className="min-w-0"><div className="truncate text-sm text-[#e9edef]">{u.name || u.email}</div><div className="truncate text-xs" style={{ color: WA.muted }}>{u.email}</div></div>
        </button>
      ))}
    </div>
  );
};
